import type { Id } from "@repo/convex/dataModel";
import { getInitials, PROJECT_LEVEL_ROUTE_PATHS } from "@repo/shared";
import {
  Archive,
  Copy,
  Ellipsis,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Settings,
  Share2,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCogIcon } from "@/components/shield-cog-icon";
import { SidebarResizeHandle } from "@/components/sidebar-resize-handle";
import { SidebarUserProfile } from "@/components/sidebar-user-profile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LongLabelTooltip } from "@/components/ui/long-label-tooltip";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { useOrganization } from "@/contexts/organization-context";
import { useTabs } from "@/contexts/tabs-context";
import { useCurrentUser } from "@/hooks/use-current-user";
import { getErrorMessage } from "@/lib/app-errors";
import {
  buildTreeIndex,
  getTreeLocationKey,
  getTreeLocationSearchParams,
  locateTreeNodeByLocation,
} from "../data/tree-navigation-state";
import {
  useCreateProjectTreeFolder,
  useProjectBySlug,
  useProjectFrameworkStructure,
  useProjectTree,
  useTreeExpandState,
} from "../hooks";
import { useConversationsFlyout } from "../hooks/use-conversations-flyout";
import { PROJECT_LEVEL_CLAUDIA_NODE_ID } from "../lib/project-level-nav-items";
import { ProjectRouteProvider } from "../routing/project-route-context";
import type { TabSearchParams, TreeItem } from "../types";
import {
  ConversationsFlyout,
  ConversationsNavTrailing,
} from "./conversations/conversations-flyout";
import { TreeNode, useTreeKeyboard } from "./tree";

interface ProjectSidebarProps {
  tabId: string;
  projectId: string;
  currentPath: string;
  searchParams: TabSearchParams;
  /**
   * Whether this tab's sidebar is the one on screen. Every open project keeps its sidebar
   * mounted (`ProjectSidebarsHost`), and the conversations flyout is portaled to the body — so
   * without this a flyout left open on one tab would hang over the next one.
   */
  isActive?: boolean;
}

const PROJECT_LEVEL_ROOT_PREFIX = "project-level:";

export function splitProjectSidebarRoots(rootFolderTrees: TreeItem[]) {
  return {
    fixedRootFolders: rootFolderTrees.filter((item) =>
      item.id.startsWith(PROJECT_LEVEL_ROOT_PREFIX)
    ),
    scrollableRootFolders: rootFolderTrees.filter(
      (item) => !item.id.startsWith(PROJECT_LEVEL_ROOT_PREFIX)
    ),
  };
}

export function ProjectSidebarActionsMenuContent({
  isPlatformAdmin,
  onOpenDfAdmin,
  onOpenProjectSettings,
}: {
  isPlatformAdmin: boolean;
  onOpenDfAdmin: () => void;
  onOpenProjectSettings: () => void;
}) {
  return (
    <DropdownMenuContent align="start" className="w-56">
      <DropdownMenuItem>
        <Pencil className="size-4 mr-2" />
        Rename Project
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Share2 className="size-4 mr-2" />
        Share Project
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Copy className="size-4 mr-2" />
        Duplicate Project
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {/* No project-scoped export exists yet — an export covers one element — and an inert
          item in this menu promised one. Export lives on the element's own ⋯ menu. */}
      <DropdownMenuItem onClick={onOpenProjectSettings}>
        <Settings className="size-4 mr-2" />
        Project Settings
      </DropdownMenuItem>
      {isPlatformAdmin && (
        <DropdownMenuItem onClick={onOpenDfAdmin}>
          <ShieldCogIcon className="size-4 mr-2" />
          Admin Portal
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem>
        <Archive className="size-4 mr-2" />
        Archive Project
      </DropdownMenuItem>
      <DropdownMenuItem className="text-destructive focus:text-destructive">
        <Trash2 className="size-4 mr-2" />
        Delete Project
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

function ProjectSidebarHeader({
  projectName,
  renderActionsMenu,
}: {
  projectName: string;
  renderActionsMenu?: () => React.ReactNode;
}) {
  const { open, toggleSidebar } = useSidebar();
  const CollapseIcon = open ? PanelLeftClose : PanelLeftOpen;
  const projectTitle = (
    <>
      <div
        data-testid="project-sidebar-title-initial"
        className="flex size-7 shrink-0 select-none items-center justify-center bg-primary"
      >
        <span className="text-[11px] font-semibold text-primary-foreground">
          {getInitials(projectName)}
        </span>
      </div>
      <LongLabelTooltip
        content={projectName}
        side="bottom"
        align="start"
        contentClassName="max-w-80 whitespace-normal break-words"
      >
        <span
          data-testid="project-sidebar-title-name"
          className="min-w-0 truncate select-none text-sm font-semibold text-foreground"
        >
          {projectName}
        </span>
      </LongLabelTooltip>
    </>
  );

  return (
    <SidebarHeader className="gap-0 p-0">
      <div className="flex h-12 items-center justify-between gap-2 px-4">
        <div className="flex min-w-0 items-center gap-2">
          {renderActionsMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    data-testid="project-sidebar-title-trigger"
                    className="flex min-w-0 flex-1 select-none items-center gap-2 rounded-sm text-left transition-colors hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                }
              >
                {projectTitle}
              </DropdownMenuTrigger>
              {renderActionsMenu()}
            </DropdownMenu>
          ) : (
            <div className="flex min-w-0 flex-1 select-none items-center gap-2">{projectTitle}</div>
          )}
          {renderActionsMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    className="shrink-0 select-none text-neutral-dim transition-colors hover:text-foreground"
                    aria-label="Project actions"
                  />
                }
              >
                <Ellipsis className="size-3.5" />
              </DropdownMenuTrigger>
              {renderActionsMenu()}
            </DropdownMenu>
          )}
        </div>
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={open}
          className="flex size-4 items-center justify-center text-neutral-dim opacity-0 transition-opacity no-touch:group-hover/sidebar:opacity-100 touch:opacity-100 max-md:opacity-100"
        >
          {/* Below md the sidebar is a drawer over a scrim (design note KiNoE): the control
              closes rather than collapses, so it reads as an X and never hides behind hover. */}
          <X className="size-4 md:hidden" />
          <CollapseIcon className="hidden size-4 md:block" />
        </button>
      </div>
      <div className="h-px bg-border mx-4" />
    </SidebarHeader>
  );
}

/**
 * ProjectSidebar - Per-project sidebar with tree navigation.
 *
 * This component is rendered once per open project tab and stays mounted
 * via the CSS show/hide pattern in ProjectContainer. This preserves:
 * - Tree expansion state
 * - Scroll position
 * - Loaded data (no re-fetch on tab switch)
 */
export function ProjectSidebar({
  tabId,
  projectId,
  currentPath,
  searchParams,
  isActive = true,
}: ProjectSidebarProps) {
  const { navigateTab, openAdminTab } = useTabs();
  const { organizationId } = useOrganization();
  const currentUser = useCurrentUser();
  const { setOpenMobile, isMobile } = useSidebar();
  const createProjectTreeFolder = useCreateProjectTreeFolder();
  const isPlatformAdmin = currentUser?.isPlatformAdmin === true;

  // Fetch project from Convex (projectId is the slug)
  const convexProject = useProjectBySlug(organizationId, projectId);
  const convexProjectId = convexProject?._id ?? null;
  const frameworkStructure = useProjectFrameworkStructure(convexProjectId);

  // Generate dynamic tree from Convex data + framework topology
  const { tree, isLoading: isTreeLoading, configError } = useProjectTree(convexProjectId);

  const [projectTreeFolderDraftParentId, setProjectTreeFolderDraftParentId] = useState<
    string | null
  >(null);
  const [projectTreeFolderDraftFrameworkNodeKey, setProjectTreeFolderDraftFrameworkNodeKey] =
    useState<string | null>(null);
  const [projectTreeFolderDraftRootFolderSlug, setProjectTreeFolderDraftRootFolderSlug] = useState<
    string | null
  >(null);
  const [projectTreeFolderDraftName, setProjectTreeFolderDraftName] = useState("");
  const [isSavingProjectTreeFolder, setIsSavingProjectTreeFolder] = useState(false);

  // Get root-folder trees from Convex data
  const rootFolderTrees = tree?.rootFolders ?? [];
  const { fixedRootFolders, scrollableRootFolders } = useMemo(
    () => splitProjectSidebarRoots(rootFolderTrees),
    [rootFolderTrees]
  );
  const treeIndex = useMemo(() => buildTreeIndex(rootFolderTrees), [rootFolderTrees]);

  // Tree expand state
  const treeExpandState = useTreeExpandState(treeIndex);
  const currentLocationKey = useMemo(
    () => getTreeLocationKey(currentPath, getTreeLocationSearchParams(searchParams)),
    [currentPath, searchParams]
  );
  const treeLocationMatch = useMemo(
    () => locateTreeNodeByLocation(treeIndex, currentLocationKey),
    [currentLocationKey, treeIndex]
  );
  const treeContainerRef = useRef<HTMLDivElement | null>(null);

  // Navigation within project (auto-closes mobile Sheet)
  const navigateWithinProject = useCallback(
    (path: string, params?: TabSearchParams) => {
      navigateTab(tabId, path, params);
      if (isMobile) {
        setOpenMobile(false);
      }
    },
    [isMobile, navigateTab, setOpenMobile, tabId]
  );

  // LAT-990: one conversations subscription per open project, held here rather than on the row,
  // so the nav dot and the flyout's list read the same query. The hook skips it entirely while
  // this tab is not the one on screen, and closes the flyout when the tab is left. It takes the
  // tab's own navigation because a project with no conversation goes straight to a new chat.
  const conversations = useConversationsFlyout({
    projectId: convexProjectId,
    isActive,
    onNavigate: navigateWithinProject,
  });
  const claudiaNavRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    treeExpandState.syncFromLocation(currentLocationKey, treeLocationMatch?.nodeId ?? null);
  }, [currentLocationKey, treeExpandState.syncFromLocation, treeLocationMatch?.nodeId]);

  useEffect(() => {
    const request = treeExpandState.scrollRequest;
    if (!request || !treeContainerRef.current) {
      return;
    }

    let frameId = 0;
    frameId = requestAnimationFrame(() => {
      const target = Array.from(
        treeContainerRef.current?.querySelectorAll<HTMLElement>("[data-tree-node-id]") ?? []
      ).find((element) => element.dataset.treeNodeId === request.nodeId);

      target?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [treeExpandState.scrollRequest]);

  const handleStartProjectTreeFolderDraft = useCallback((item: TreeItem) => {
    setProjectTreeFolderDraftParentId(item.id);
    setProjectTreeFolderDraftFrameworkNodeKey(item.metadata?.parentFrameworkNodeKey ?? null);
    setProjectTreeFolderDraftRootFolderSlug(item.metadata?.rootFolderSlug ?? null);
    setProjectTreeFolderDraftName("");
  }, []);

  const handleCancelProjectTreeFolderDraft = useCallback(() => {
    if (isSavingProjectTreeFolder) {
      return;
    }
    setProjectTreeFolderDraftParentId(null);
    setProjectTreeFolderDraftFrameworkNodeKey(null);
    setProjectTreeFolderDraftRootFolderSlug(null);
    setProjectTreeFolderDraftName("");
  }, [isSavingProjectTreeFolder]);

  const handleSubmitProjectTreeFolderDraft = useCallback(async () => {
    if (
      !convexProjectId ||
      !projectTreeFolderDraftFrameworkNodeKey ||
      !projectTreeFolderDraftRootFolderSlug
    ) {
      return;
    }

    const name = projectTreeFolderDraftName.trim();
    if (!name) {
      handleCancelProjectTreeFolderDraft();
      return;
    }

    setIsSavingProjectTreeFolder(true);
    try {
      const createdFolder = await createProjectTreeFolder({
        projectId: convexProjectId,
        parentFrameworkNodeKey: projectTreeFolderDraftFrameworkNodeKey,
        name,
      });
      toast.success("Folder created");
      handleCancelProjectTreeFolderDraft();
      navigateWithinProject(`/${projectTreeFolderDraftRootFolderSlug}/${createdFolder.slug}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create folder"));
    } finally {
      setIsSavingProjectTreeFolder(false);
    }
  }, [
    convexProjectId,
    createProjectTreeFolder,
    handleCancelProjectTreeFolderDraft,
    navigateWithinProject,
    projectTreeFolderDraftFrameworkNodeKey,
    projectTreeFolderDraftName,
    projectTreeFolderDraftRootFolderSlug,
  ]);

  const renderRootFolderTreeNode = useCallback(
    (rootFolderTree: TreeItem) => {
      // The Claudia row is the one nav row that opens something instead of going somewhere: it
      // toggles the conversations flyout (or, with no conversation to pick, opens a new chat),
      // and keeps its route-active styling from the location.
      const isClaudiaRow = rootFolderTree.id === PROJECT_LEVEL_CLAUDIA_NODE_ID;

      return (
        <TreeNode
          item={rootFolderTree}
          projectId={convexProjectId as Id<"projects">}
          treeExpandState={treeExpandState}
          rowRef={isClaudiaRow ? claudiaNavRowRef : undefined}
          onActivate={isClaudiaRow ? conversations.activate : undefined}
          trailing={
            isClaudiaRow ? (
              <ConversationsNavTrailing
                needsYou={conversations.needsYouCount > 0}
                opensFlyout={conversations.opensFlyout}
                showChevron={
                  conversations.isOpen ||
                  treeExpandState.activeNodeId === PROJECT_LEVEL_CLAUDIA_NODE_ID
                }
              />
            ) : undefined
          }
          projectTreeFolderDraftParentId={projectTreeFolderDraftParentId}
          projectTreeFolderDraftName={projectTreeFolderDraftName}
          isSavingProjectTreeFolder={isSavingProjectTreeFolder}
          onProjectTreeFolderDraftChange={setProjectTreeFolderDraftName}
          onProjectTreeFolderDraftSubmit={() => {
            void handleSubmitProjectTreeFolderDraft();
          }}
          onProjectTreeFolderDraftCancel={handleCancelProjectTreeFolderDraft}
          onStartProjectTreeFolderDraft={handleStartProjectTreeFolderDraft}
        />
      );
    },
    [
      conversations.activate,
      conversations.isOpen,
      conversations.needsYouCount,
      conversations.opensFlyout,
      convexProjectId,
      handleCancelProjectTreeFolderDraft,
      handleStartProjectTreeFolderDraft,
      handleSubmitProjectTreeFolderDraft,
      isSavingProjectTreeFolder,
      projectTreeFolderDraftName,
      projectTreeFolderDraftParentId,
      treeExpandState,
    ]
  );

  // Tree keyboard navigation
  const keyboardOptions = useMemo(
    () => ({
      expandNode: treeExpandState.expandManualNode,
      collapseNode: treeExpandState.collapseManualNode,
    }),
    [treeExpandState.expandManualNode, treeExpandState.collapseManualNode]
  );
  const keyboardTreeRef = useTreeKeyboard(keyboardOptions);
  const treeRef = useCallback(
    (node: HTMLDivElement | null) => {
      treeContainerRef.current = node;
      keyboardTreeRef(node);
    },
    [keyboardTreeRef]
  );

  // Loading state
  if (convexProject === undefined || isTreeLoading) {
    return (
      <Sidebar side="left" collapsible="offcanvas" className="group/sidebar bg-sidebar">
        <ProjectSidebarHeader projectName="Loading..." />
        <SidebarUserProfile />
        <SidebarResizeHandle />
      </Sidebar>
    );
  }

  // Project not found
  if (!convexProject) {
    return (
      <Sidebar side="left" collapsible="offcanvas" className="group/sidebar bg-sidebar">
        <ProjectSidebarHeader projectName="Project not found" />
        <SidebarUserProfile />
        <SidebarResizeHandle />
      </Sidebar>
    );
  }

  if (configError) {
    return (
      <Sidebar side="left" collapsible="offcanvas" className="group/sidebar bg-sidebar">
        <ProjectSidebarHeader
          projectName={convexProject.name}
          renderActionsMenu={() => (
            <ProjectSidebarActionsMenuContent
              isPlatformAdmin={isPlatformAdmin}
              onOpenDfAdmin={openAdminTab}
              onOpenProjectSettings={() =>
                navigateWithinProject(PROJECT_LEVEL_ROUTE_PATHS.projectQualityGuardConfiguration)
              }
            />
          )}
        />
        <SidebarContent>
          <SidebarGroup>
            <div className="px-3 py-2 text-sm text-destructive">{configError}</div>
          </SidebarGroup>
        </SidebarContent>
        <SidebarUserProfile />
        <SidebarResizeHandle />
      </Sidebar>
    );
  }

  if (rootFolderTrees.length === 0) {
    return (
      <Sidebar side="left" collapsible="offcanvas" className="group/sidebar bg-sidebar">
        <ProjectSidebarHeader
          projectName={convexProject.name}
          renderActionsMenu={() => (
            <ProjectSidebarActionsMenuContent
              isPlatformAdmin={isPlatformAdmin}
              onOpenDfAdmin={openAdminTab}
              onOpenProjectSettings={() =>
                navigateWithinProject(PROJECT_LEVEL_ROUTE_PATHS.projectQualityGuardConfiguration)
              }
            />
          )}
        />
        <SidebarContent>
          <SidebarGroup>
            <div className="px-3 py-2 text-sm text-destructive">
              Framework configuration invalid: no root folders are available.
            </div>
          </SidebarGroup>
        </SidebarContent>
        <SidebarUserProfile />
        <SidebarResizeHandle />
      </Sidebar>
    );
  }

  return (
    <ProjectRouteProvider
      currentPath={currentPath}
      frameworkStructure={frameworkStructure}
      navigate={navigateWithinProject}
    >
      <Sidebar side="left" collapsible="offcanvas" className="group/sidebar bg-sidebar">
        <ProjectSidebarHeader
          projectName={convexProject.name}
          renderActionsMenu={() => (
            <ProjectSidebarActionsMenuContent
              isPlatformAdmin={isPlatformAdmin}
              onOpenDfAdmin={openAdminTab}
              onOpenProjectSettings={() =>
                navigateWithinProject(PROJECT_LEVEL_ROUTE_PATHS.projectQualityGuardConfiguration)
              }
            />
          )}
        />

        <SidebarContent className="overflow-hidden">
          <SidebarGroup className="min-h-0 flex-1 p-0">
            <div
              ref={treeRef}
              role="tree"
              aria-label="Project Navigation"
              className="flex min-h-0 flex-1 flex-col"
            >
              {fixedRootFolders.length > 0 ? (
                <div data-testid="project-level-nav" className="shrink-0 px-2 pt-2">
                  {fixedRootFolders.map((rootFolderTree) => (
                    <div key={rootFolderTree.id}>{renderRootFolderTreeNode(rootFolderTree)}</div>
                  ))}
                </div>
              ) : null}

              {fixedRootFolders.length > 0 && scrollableRootFolders.length > 0 ? (
                <SidebarSeparator className="mt-2 mb-0 shrink-0" />
              ) : null}

              <ScrollShadow
                colorScheme="sidebar"
                className="relative flex min-h-0 flex-1 flex-col"
                contentClassName="px-2 pt-2"
                viewportProps={{
                  "data-scroll-affordance-container": "project-sidebar",
                }}
              >
                <div>
                  {scrollableRootFolders.map((rootFolderTree, index) => (
                    <div key={rootFolderTree.id}>
                      {renderRootFolderTreeNode(rootFolderTree)}
                      {index < scrollableRootFolders.length - 1 ? (
                        <SidebarSeparator className="my-2" />
                      ) : null}
                    </div>
                  ))}
                </div>
              </ScrollShadow>
            </div>
          </SidebarGroup>
        </SidebarContent>
        <SidebarUserProfile />
        <SidebarResizeHandle />
      </Sidebar>
      <ConversationsFlyout
        open={conversations.isOpen}
        onOpenChange={conversations.setIsOpen}
        anchorRef={claudiaNavRowRef}
        items={conversations.items}
        isLoading={conversations.isLoading}
        currentPath={currentPath}
        onNavigate={navigateWithinProject}
      />
    </ProjectRouteProvider>
  );
}
